import { Injectable, NgZone, effect, inject, signal } from "@angular/core";
import { BoatTelemetryStore } from "../sim/boat-telemetry.store";
import type { BoatImpactEvent, BoatTelemetry } from "../../../simulation-core";
import { APP_CONFIG } from "../app-config";
import {
  CASTOFFPRO_AUDIO_MUTED,
  CASTOFFPRO_AUDIO_VOLUME,
} from "../storage/storage-keys";

/**
 * Mixed audio scene for the simulator. Three persistent loops + impact
 * one-shots, all routed through a master gain node so a single mute /
 * volume slider controls everything.
 *
 * Layers:
 *   - **background**: harbour ambience, constant volume.
 *   - **wind**: gain rises with true wind speed (silent in calm, full at gale).
 *   - **engine**: gain + playbackRate driven by |throttle|; buffer is
 *     swapped when the boat's engineType changes (inboard ↔ outboard).
 *
 * Impacts (dock / ground / boat-on-boat) are synthesised live from a
 * filtered noise burst — no audio asset needed and the resulting "thud"
 * scales naturally with the contact speed.
 *
 * Browser autoplay policy: the AudioContext is created up-front but stays
 * `suspended` until the first user gesture (click / pointer / key). The
 * service hooks one-shot listeners on `window` to resume on any input.
 */
@Injectable({ providedIn: "root" })
export class AudioService {
  private readonly config = inject(APP_CONFIG);
  private readonly zone = inject(NgZone);
  private readonly telemetryStore = inject(BoatTelemetryStore);

  /** Master mute toggle; persisted in localStorage. */
  readonly muted = signal<boolean>(this.readMuted());
  /** 0..1 master volume; persisted in localStorage. */
  readonly volume = signal<number>(this.readVolume());

  private ctx: AudioContext | null = null;
  private master: GainNode | null = null;
  private noiseBuffer: AudioBuffer | null = null;

  private wind: LoopVoice | null = null;
  private engine: LoopVoice | null = null;
  private currentEngineType: "inboard" | "outboard" | null = null;

  private buffers: Partial<Record<SoundKey, AudioBuffer>> = {};
  private initPromise: Promise<void> | null = null;
  /** True when at least one user gesture has resumed the context. */
  private resumed = false;

  constructor() {
    // Push gain / mute changes down to the master node whenever signals tick.
    effect(() => {
      const v = this.muted() ? 0 : this.volume();
      if (this.master && this.ctx) {
        this.master.gain.setTargetAtTime(v, this.ctx.currentTime, 0.05);
      }
      this.writeSettings();
    });

    // Drive wind + engine gains from telemetry. Runs outside Angular's
    // change detection: the telemetry signal updates every frame.
    this.zone.runOutsideAngular(() => {
      effect(() => {
        const t = this.telemetryStore.state();
        if (t) this.applyTelemetry(t);
      });
    });
  }

  /**
   * Bootstrap the audio context + load assets. Safe to call repeatedly;
   * the heavy work runs only once. Should be invoked from a user-gesture
   * handler (button click, settings open) so the context can resume.
   */
  async init(): Promise<void> {
    if (this.initPromise) return this.initPromise;
    this.initPromise = this.bootstrap();
    return this.initPromise;
  }

  /**
   * Pump impact events through the synth. Call once per frame (or per
   * telemetry tick) — events with the same kind in quick succession are
   * already debounced by the producer side.
   */
  handleImpacts(events: readonly BoatImpactEvent[]): void {
    if (events.length === 0) return;
    if (!this.ctx || !this.master) return;
    if (this.muted()) return;
    for (const ev of events) this.playImpact(ev);
  }

  toggleMute(): void {
    this.muted.update((v) => !v);
    // First unmute is also the natural moment to boot the audio engine.
    if (!this.muted() && !this.initPromise) {
      void this.init();
    }
  }

  setVolume(v: number): void {
    const clamped = Math.max(0, Math.min(1, v));
    this.volume.set(clamped);
  }

  // --- internals -----------------------------------------------------------

  private async bootstrap(): Promise<void> {
    const Ctx: typeof AudioContext =
      window.AudioContext ??
      (window as unknown as { webkitAudioContext: typeof AudioContext })
        .webkitAudioContext;
    if (!Ctx) {
      console.warn("[Audio] WebAudio not supported");
      return;
    }
    const ctx = new Ctx();
    const master = ctx.createGain();
    master.gain.value = this.muted() ? 0 : this.volume();
    master.connect(ctx.destination);
    this.ctx = ctx;
    this.master = master;
    this.noiseBuffer = makeNoiseBuffer(ctx, 1.0);

    this.installResumeHooks();

    // Loaders run in parallel; missing files degrade gracefully.
    const loads: Array<Promise<void>> = [];
    for (const key of SOUND_KEYS) {
      loads.push(
        loadBuffer(ctx, SOUND_URLS[key]).then((buf) => {
          if (buf) this.buffers[key] = buf;
        }),
      );
    }
    await Promise.all(loads);

    // Spin up the persistent loops at zero gain — they fade in from the
    // telemetry effect once the first frame ticks through. Background
    // ambience runs at a fixed 50% so it sits below engine + wind layers.
    this.makeLoop("background", 0.5);
    this.wind = this.makeLoop("wind_strong", 0.0);
    this.engine = null; // engine voice is created lazily (needs engineType).
  }

  private installResumeHooks(): void {
    if (!this.ctx) return;
    const ctx = this.ctx;
    const resume = (): void => {
      if (this.resumed) return;
      this.resumed = true;
      void ctx.resume();
    };
    const opts: AddEventListenerOptions = { once: true, capture: true };
    window.addEventListener("pointerdown", resume, opts);
    window.addEventListener("keydown", resume, opts);
    window.addEventListener("touchstart", resume, opts);
  }

  /** Build a looping voice from a preloaded buffer. */
  private makeLoop(key: SoundKey, gain: number): LoopVoice | null {
    if (!this.ctx || !this.master) return null;
    const buf = this.buffers[key];
    if (!buf) return null;
    return startLoop(this.ctx, this.master, buf, gain);
  }

  private applyTelemetry(t: BoatTelemetry): void {
    if (!this.ctx) return;
    const now = this.ctx.currentTime;
    // Wind: fade in between 3 and 14 m/s. Below the threshold the harbour
    // ambience is enough; above it we want an audible whistle.
    if (this.wind) {
      const w = clamp01((t.tws - 3) / (14 - 3));
      this.wind.gain.gain.setTargetAtTime(w * 0.85, now, 0.4);
    }
    // Engine voice: lazily create + swap when boat type changes. Only
    // commit `currentEngineType` if the loop actually started — otherwise
    // we'd remember a "pending" type and never retry once buffers load.
    if (this.currentEngineType !== t.engineType) {
      const key: SoundKey =
        t.engineType === "outboard" ? "engine_outboard" : "engine_inboard";
      const next = this.makeLoop(key, 0.0);
      if (next) {
        this.engine?.dispose();
        this.engine = next;
        this.currentEngineType = t.engineType;
      }
    }
    if (this.engine) {
      const th = Math.abs(t.throttle);
      // Audible idle (~0.2) so the engine is heard even when stopped at the
      // pier; ramps up to ~0.95 at full throttle.
      const g = 0.2 + th * 0.75;
      // Pitch bend: 0.85× at idle, 1.4× at full ahead.
      const rate = 0.85 + th * 0.55;
      this.engine.gain.gain.setTargetAtTime(g, now, 0.25);
      this.engine.source.playbackRate.setTargetAtTime(rate, now, 0.3);
    }
  }

  private playImpact(ev: BoatImpactEvent): void {
    if (!this.ctx || !this.master) return;
    const ctx = this.ctx;
    const now = ctx.currentTime;
    const speed = Math.min(ev.speed, 5);
    // Map 0.4..5 m/s → 0.15..0.9 gain.
    const peak = 0.15 + clamp01((speed - 0.4) / 4.6) * 0.75;

    // Prefer the sampled collision.mp3 when available — much richer than
    // the synthesised filtered-noise fallback. Pitch is bent slightly per
    // surface so dock / ground / boat still feel distinct.
    const sample = this.buffers["collision"];
    if (sample) {
      const src = ctx.createBufferSource();
      src.buffer = sample;
      const gain = ctx.createGain();
      gain.gain.value = peak;
      const rate =
        ev.kind === "ground"
          ? 0.7
          : ev.kind === "dock"
            ? 1.0
            : ev.kind === "embankment"
              ? 1.15
              : 0.85;
      src.playbackRate.value = rate;
      src.connect(gain);
      gain.connect(this.master);
      src.start(now);
      return;
    }

    if (!this.noiseBuffer) return;
    const src = ctx.createBufferSource();
    src.buffer = this.noiseBuffer;
    const filter = ctx.createBiquadFilter();
    const gain = ctx.createGain();

    if (ev.kind === "ground") {
      // Muffled thud — low-pass + slow envelope. Pitch drops with intensity.
      filter.type = "lowpass";
      filter.frequency.value = 220 + speed * 30;
      filter.Q.value = 0.7;
      const dur = 0.55;
      gain.gain.setValueAtTime(0, now);
      gain.gain.linearRampToValueAtTime(peak, now + 0.01);
      gain.gain.exponentialRampToValueAtTime(0.001, now + dur);
      src.stop(now + dur + 0.05);
    } else if (ev.kind === "dock") {
      // Woody knock — narrow band-pass around 380 Hz, sharp attack/decay.
      filter.type = "bandpass";
      filter.frequency.value = 380;
      filter.Q.value = 6;
      const dur = 0.32;
      gain.gain.setValueAtTime(0, now);
      gain.gain.linearRampToValueAtTime(peak, now + 0.005);
      gain.gain.exponentialRampToValueAtTime(0.001, now + dur);
      src.stop(now + dur + 0.05);
    } else if (ev.kind === "embankment") {
      filter.type = "bandpass";
      filter.frequency.value = 520;
      filter.Q.value = 8;
      const dur = 0.24;
      gain.gain.setValueAtTime(0, now);
      gain.gain.linearRampToValueAtTime(peak * 0.9, now + 0.004);
      gain.gain.exponentialRampToValueAtTime(0.001, now + dur);
      src.stop(now + dur + 0.05);
    } else {
      // Boat-on-boat: dull rubbery bump.
      filter.type = "lowpass";
      filter.frequency.value = 600;
      const dur = 0.4;
      gain.gain.setValueAtTime(0, now);
      gain.gain.linearRampToValueAtTime(peak * 0.7, now + 0.01);
      gain.gain.exponentialRampToValueAtTime(0.001, now + dur);
      src.stop(now + dur + 0.05);
    }

    src.connect(filter);
    filter.connect(gain);
    gain.connect(this.master);
    src.start(now);
  }

  private readMuted(): boolean {
    try {
      const raw = localStorage.getItem(CASTOFFPRO_AUDIO_MUTED);
      if (raw == null) return this.config.audioMutedByDefault;
      return raw === "1";
    } catch {
      return this.config.audioMutedByDefault;
    }
  }

  private readVolume(): number {
    try {
      const raw = localStorage.getItem(CASTOFFPRO_AUDIO_VOLUME);
      if (raw == null) return 0.7;
      const v = Number(raw);
      return Number.isFinite(v) ? Math.max(0, Math.min(1, v)) : 0.7;
    } catch {
      return 0.7;
    }
  }

  private writeSettings(): void {
    try {
      localStorage.setItem(CASTOFFPRO_AUDIO_MUTED, this.muted() ? "1" : "0");
      localStorage.setItem(CASTOFFPRO_AUDIO_VOLUME, String(this.volume()));
    } catch {
      // ignore quota / private mode failures
    }
  }
}

interface LoopVoice {
  source: AudioBufferSourceNode;
  gain: GainNode;
  dispose(): void;
}

const SOUND_KEYS = [
  "background",
  "wind_strong",
  "engine_inboard",
  "engine_outboard",
  "collision",
] as const;
type SoundKey = (typeof SOUND_KEYS)[number];

const SOUND_URLS: Record<SoundKey, string> = {
  background: "sounds/background.mp3",
  wind_strong: "sounds/wind_strong.mp3",
  engine_inboard: "sounds/engine_inboard.mp3",
  engine_outboard: "sounds/engine_outboard.mp3",
  collision: "sounds/collision.mp3",
};

async function loadBuffer(
  ctx: AudioContext,
  url: string,
): Promise<AudioBuffer | null> {
  try {
    const res = await fetch(url);
    if (!res.ok) {
      console.warn(`[Audio] ${url} → ${res.status}`);
      return null;
    }
    const data = await res.arrayBuffer();
    return await ctx.decodeAudioData(data);
  } catch (err) {
    console.warn(`[Audio] failed to load ${url}:`, err);
    return null;
  }
}

function startLoop(
  ctx: AudioContext,
  dest: AudioNode,
  buffer: AudioBuffer,
  initialGain: number,
): LoopVoice {
  const source = ctx.createBufferSource();
  source.buffer = buffer;
  source.loop = true;
  const gain = ctx.createGain();
  gain.gain.value = initialGain;
  source.connect(gain);
  gain.connect(dest);
  source.start(0);
  return {
    source,
    gain,
    dispose(): void {
      try {
        source.stop();
      } catch {
        /* already stopped */
      }
      source.disconnect();
      gain.disconnect();
    },
  };
}

/** White noise buffer of the given duration in seconds. */
function makeNoiseBuffer(ctx: AudioContext, seconds: number): AudioBuffer {
  const length = Math.max(1, Math.floor(ctx.sampleRate * seconds));
  const buf = ctx.createBuffer(1, length, ctx.sampleRate);
  const data = buf.getChannelData(0);
  for (let i = 0; i < length; i++) data[i] = Math.random() * 2 - 1;
  return buf;
}

function clamp01(v: number): number {
  return v < 0 ? 0 : v > 1 ? 1 : v;
}
